import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcCafe } from './CalccafeContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './RelatorioCafe.css';

const RelatorioCafe = () => {
  const { dados, setDados } = useCalcCafe();
  const navigate = useNavigate();

  // Funções auxiliares para formatação consistente
  const formatCurrency = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatNumber = (value, decimals = 2) => parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  // Extrai os dados de forma segura, com fallbacks para evitar erros
  const producao = dados.producao || {};
  const resProducao = dados.resultadosProducao || {};
  const capital = dados.capital || {};
  const resCapital = dados.resultadosCapital || {};
  const custos = dados.custos || {};
  const resCustos = dados.resultadosCustos || {};
  const receita = dados.receita || {};
  const resReceita = dados.resultadosReceita || {};

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const primaryColor = [111, 78, 55]; // Marrom #6f4e37
    const darkColor = [62, 39, 35];     // Marrom escuro #3E2723

    // Cabeçalho
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text("Relatório Final - CalcCafé", 105, 12, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (producao.dataSelecionada) {
        doc.text(`Análise para ${new Date(producao.dataSelecionada).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}`, 105, 20, { align: 'center' });
    }

    let startY = 40;

    // Função para adicionar seções e pular página se necessário
    const addSection = (title, head, body) => {
        const tableHeight = (body.length + 1) * 10 + 20; // Estimativa de altura da tabela
        if (startY + tableHeight > 280) { // Se não couber na página
            doc.addPage();
            startY = 20;
        }
        autoTable(doc, {
            startY: startY,
            head: [head],
            body: body,
            theme: 'grid',
            headStyles: { fillColor: darkColor },
            didDrawPage: (data) => {
                startY = data.cursor.y + 10; // Atualiza a posição Y
            }
        });
    };
    
    // Dados de Produção
    addSection("Produção e Produtividade", ['Indicador', 'Valor'], [
        ['Produção Total', `${formatNumber((Number(producao.producaoTotal)/100) || 0)} sacas`],
        ['Área Plantada', `${formatNumber((Number(producao.areaPlantada)/100) || 0)} ha`],
        ['Sacas por Hectare', `${formatNumber(resProducao.sacasPorHectare)} sc/ha`],
    ]);
    
    // Dados de Capital
    addSection("Capital Investido", ['Indicador', 'Valor'], [
        ['Capital Total Investido', formatCurrency(resCapital.capitalTotalInvestido)],
        ['Rentabilidade Esperada do Capital', formatCurrency(resCapital.rentabilidadeEsperadaCapital)],
        ['Depreciação', formatCurrency(resCapital.depreciacao)],
    ]);

    // Dados de Custos
    addSection("Custos de Produção", ['Indicador', 'Valor'], [
        ['Custo Operacional Total', formatCurrency(resCustos.custoOperacionalCafe)],
        ['Custo Total de Produção', formatCurrency(resCustos.custoTotalCafe)],
        ['Custo Operacional por Saca', formatCurrency(resCustos.custoOperacionalPorSaca)],
        ['Custo Total por Saca', formatCurrency(resCustos.custoTotalPorSaca)],
    ]);

    // Dados de Receita
    addSection("Receita e Rentabilidade", ['Indicador', 'Valor'], [
        ['Preço da Saca', formatCurrency((Number(receita.precoSaca)/100) || 0)],
        ['Receita Total', formatCurrency(resReceita.receitaTotalCafe)],
        ['Lucro Operacional', formatCurrency(resReceita.lucroOperacionalCafe)],
        ['Lucro Líquido (Total)', formatCurrency(resReceita.lucroTotalCafe)],
        ['Ponto de Equilíbrio', `${formatNumber(resReceita.pontoEquilibrioSacas)} sacas`],
        ['Rentabilidade (Cultura vs Capital)', `${formatNumber(resReceita.rentabilidadeCulturaCapitalInvestido)} %`],
    ]);

    doc.save("relatorio_final_calccafe.pdf");
  };

  const handleLimparTodosDados = () => {
    if (window.confirm("ATENÇÃO! Isso limpará TODOS os dados inseridos em TODAS as telas do CalcCafé. Deseja continuar?")) {
      setDados({});
      localStorage.removeItem('calcCafeData');
      alert("Todos os dados do CalcCafé foram limpos.");
      navigate('/calccafe/app');
    }
  };

  return (
    <div className="cafe-relatorio-container">
      <div className="cafe-relatorio-card">
        <h1>Relatório Final</h1>
        <p className="cafe-relatorio-subtitle">
          Resumo consolidado dos seus resultados para a data de <strong>{producao.dataSelecionada ? new Date(producao.dataSelecionada).toLocaleDateString('pt-BR', {timeZone: 'UTC'}) : 'N/A'}</strong>.
        </p>

        <div className="relatorio-grid">
          <div className="relatorio-section">
            <h2>Produção e Produtividade</h2>
            <div className="relatorio-item"><span>Sacas por Hectare</span><strong>{formatNumber(resProducao.sacasPorHectare)} sc/ha</strong></div>
            <div className="relatorio-item"><span>Sacas por Colaborador</span><strong>{formatNumber(resProducao.sacasPorColaborador)} sc/colab</strong></div>
          </div>

          <div className="relatorio-section">
            <h2>Capital e Depreciação</h2>
            <div className="relatorio-item"><span>Capital Total Investido</span><strong>{formatCurrency(resCapital.capitalTotalInvestido)}</strong></div>
            <div className="relatorio-item"><span>Rentabilidade do Capital</span><strong>{formatCurrency(resCapital.rentabilidadeEsperadaCapital)}</strong></div>
            <div className="relatorio-item"><span>Depreciação</span><strong>{formatCurrency(resCapital.depreciacao)}</strong></div>
          </div>

          <div className="relatorio-section">
            <h2>Custos de Produção</h2>
            <div className="relatorio-item"><span>Custo Operacional Total</span><strong>{formatCurrency(resCustos.custoOperacionalCafe)}</strong></div>
            <div className="relatorio-item"><span>Custo Total de Produção</span><strong>{formatCurrency(resCustos.custoTotalCafe)}</strong></div>
            <div className="relatorio-item"><span>Custo Operacional por Saca</span><strong>{formatCurrency(resCustos.custoOperacionalPorSaca)}</strong></div>
            <div className="relatorio-item"><span>Custo Total por Saca</span><strong>{formatCurrency(resCustos.custoTotalPorSaca)}</strong></div>
          </div>
          
          <div className="relatorio-section">
            <h2>Receita e Rentabilidade</h2>
            <div className="relatorio-item"><span>Receita Bruta Total</span><strong>{formatCurrency(resReceita.receitaTotalCafe)}</strong></div>
            <div className="relatorio-item"><span>Lucro Líquido (Total)</span><strong>{formatCurrency(resReceita.lucroTotalCafe)}</strong></div>
            <div className="relatorio-item"><span>Ponto de Equilíbrio</span><strong>{formatNumber(resReceita.pontoEquilibrioSacas)} Sacas</strong></div>
            <div className="relatorio-item"><span>Rentabilidade (Cultura vs Capital)</span><strong>{formatNumber(resReceita.rentabilidadeCulturaCapitalInvestido)} %</strong></div>
          </div>
        </div>

        <div className="relatorio-actions">
            <button className="relatorio-button pdf" onClick={handleDownloadPDF}>Baixar Relatório Completo (PDF)</button>
        </div>

        <div className="relatorio-navigation">
          <button className="relatorio-button back" onClick={() => navigate('/calccafe/app/receitaCafe')}>Voltar para Receita</button>
          <button className="relatorio-button clean" onClick={handleLimparTodosDados}>Limpar Todos os Dados</button>
        </div>
      </div>
    </div>
  );
};

export default RelatorioCafe;
